/**
 * Team Inbox — EMAIL channel mapping + lenses (spec PART 3, AD-014/015/021).
 *
 * PURE module (no Firebase / React) so the Node test runner can exercise it.
 * The Firestore subscription itself lives in index.tsx (it calls
 * `listenEmailQueue` from lib/triageStore); here we only map the
 * `draft_contacts` email shape (the Shared Data Contract) into the existing
 * `InboxConversation` view-model so the one S1 list renders both channels,
 * plus the channel lens (All / WhatsApp / Email) and the reply-param builder.
 *
 * `import type` is erased by esbuild, so importing DraftContact from the
 * Firebase-backed triageStore keeps this module SDK-free.
 */
import type { DraftContact } from '../../../lib/triageStore';
import type {
  EmailItem,
  InboxChannel,
  InboxConversation,
} from './types';

/** The single public inbound address replies go out from (AD-014). */
export const CARE_EMAIL = 'care@aayatanveda.in';

/** PURE — epoch ms from a Firestore Timestamp / number / ISO string / null. */
export function toMs(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return Number.isNaN(t) ? null : t;
  }
  const o = v as { toMillis?: () => number; seconds?: number };
  if (typeof o.toMillis === 'function') return o.toMillis();
  if (typeof o.seconds === 'number') return o.seconds * 1000;
  return null;
}

/** PURE — first two initials of a display string ("Sunita Agarwal" → "SA"). */
export function emailInitials(name: string): string {
  return (
    String(name || '')
      .replace(/[<>@].*$/, '') // drop the address part of "Name <a@b>"
      .split(/[\s._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join('') || '✉'
  );
}

/** PURE — collapse a body to a single-line list snippet. */
export function emailSnippet(text: string, max = 80): string {
  const flat = String(text || '').replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat || '—';
}

/**
 * PURE — unescape the handful of HTML entities a triage body can carry.
 * The triage writer (`routeInboundEmail`) stores `ExtractedMarkdownMessage`,
 * which arrives HTML-ESCAPED (`&lt;html&gt;…`) — so a raw HTML payload looks
 * like inert text until we unescape it. Numeric entities are covered too.
 */
export function unescapeHtmlEntities(s: string): string {
  return String(s || '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&#x0*27;/gi, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&#(\d+);/g, (_m, n) => String.fromCodePoint(Number(n)))
    .replace(/&#x([0-9a-f]+);/gi, (_m, n) => String.fromCodePoint(parseInt(n, 16)))
    .replace(/&amp;/g, '&'); // last — so "&amp;lt;" → "&lt;" not "<"
}

/** PURE — does this string contain real HTML tags (after unescaping)? */
export function looksLikeHtml(s: string): boolean {
  return /<(?:html|body|div|p|br|a|table|tr|td|span|h[1-6]|ul|ol|li|img|b|strong|i|em)\b[^>]*>/i.test(
    s,
  );
}

/**
 * PURE — is this body Brevo's flattened markdown (NOT real HTML)? An HTML email
 * laid out in tables comes back as markdown table rows (`| … |`, `| --- |`) and
 * `[label](url)` links, sometimes with a stray inline `<br>`. That stray tag
 * must NOT route the body down the raw-HTML path (it renders the pipes + link
 * syntax as gibberish) — markdown detection wins so cleanInboundMarkdown runs.
 */
export function looksLikeBrevoMarkdown(s: string): boolean {
  const t = String(s || '');
  return /(^|\n)\s*\|.*\|/.test(t) || /\[[^\]]+\]\([^)]+\)/.test(t);
}

/**
 * PURE — de-noise Brevo's `ExtractedMarkdownMessage`. HTML emails laid out with
 * spacer/layout tables (Hostinger, most ESPs) convert to markdown as page after
 * page of EMPTY table scaffolding — `|     |     |` and `| --- | --- |` rows —
 * which renders as gibberish in the read pane. We DROP rows that are pure table
 * scaffolding (only pipes / dashes / colons / spaces), UNWRAP real content cells
 * (`| [Activate](url) |` → `[Activate](url)`, links survive for linkifyText),
 * and collapse the blank-line runs the stripping leaves behind. Plain-text
 * bodies (no table rows) pass through essentially unchanged.
 */
export function cleanInboundMarkdown(s: string): string {
  // Brevo's markdown carries the odd inline <br> from the source HTML — treat
  // it as a line break so the unwrapped text reads naturally (and so it can't
  // masquerade as "real HTML" downstream).
  const normalised = String(s || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\\\|/g, ' · '); // escaped pipe (`\|`) was a visual separator
  const out: string[] = [];
  for (const raw of normalised.split('\n')) {
    const line = raw.replace(/\s+$/, ''); // rtrim
    const trimmed = line.trim();
    // pure table scaffolding / empty cells (`| --- |`, `|   |   |`) → drop
    if (trimmed !== '' && /^[\s|:-]*$/.test(trimmed) && /[|-]/.test(trimmed)) {
      continue;
    }
    // a content table row (`| cell | cell |`) → unwrap to its cell text
    if (trimmed.startsWith('|') && trimmed.includes('|')) {
      const cells = trimmed
        .replace(/^\||\|$/g, '')
        .split('|')
        .map((c) => c.trim())
        .filter(Boolean);
      if (cells.length === 0) continue;
      out.push(cells.join('  '));
      continue;
    }
    out.push(line);
  }
  return out
    .join('\n')
    .replace(/[ \t]*\|[ \t]*$/gm, '') // drop a dangling cell-edge pipe a <br> split off
    .replace(/^#{1,6}[ \t]+/gm, '') // markdown heading marker → plain heading text
    .replace(/[ \t]*\\+[ \t]*$/gm, '') // strip a stray trailing backslash
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * PURE — resolve the rendered body for an email triage record into the
 * two shapes the read pane understands: `{ bodyText, bodyHtml }`.
 *
 * Field reality (verified against live `draft_contacts`):
 *  - FULL-CONTRACT docs (known-contact / Part-1 path) carry `bodyText` +
 *    `bodyHtml` directly → use them as-is.
 *  - MINIMAL TRIAGE docs (`routeInboundEmail` path) carry ONLY `firstMessage`
 *    (the `ExtractedMarkdownMessage`), often HTML-ESCAPED. Nothing mapped it
 *    before → the pane showed "(No message body.)". We now derive from
 *    `firstMessage`: unescape entities, and if the result is HTML, route it
 *    through `bodyHtml` (the pane SANITISES it via DOMPurify); otherwise keep
 *    it as text (rendered safely + linkified by the pane).
 *
 * `bodyHtml` here is the RAW (untrusted) HTML — sanitisation happens at the
 * render seam in EmailThread.tsx (DOMPurify). We never inject unsanitised.
 */
export function deriveEmailBody(d: DraftContact): {
  bodyText: string;
  bodyHtml: string | null;
} {
  // 1) explicit contract fields win (full Part-1 / known-contact docs). When a
  //    real HTML body exists the pane renders it; bodyText is only the snippet
  //    source, so de-noise its Brevo markdown either way.
  const text = typeof d.bodyText === 'string' ? d.bodyText : '';
  const html = typeof d.bodyHtml === 'string' ? d.bodyHtml : null;
  if (text || html) {
    return { bodyText: cleanInboundMarkdown(text), bodyHtml: html };
  }

  // 2) fall back to firstMessage (minimal / legacy triage docs that never
  //    stored bodyText/bodyHtml — body lives in firstMessage only)
  const raw = typeof d.firstMessage === 'string' ? d.firstMessage : '';
  if (!raw.trim()) return { bodyText: '', bodyHtml: null };

  const unescaped = unescapeHtmlEntities(raw);
  // Brevo markdown wins over a stray inline <br> — clean the table noise and
  // render as text so links linkify (NOT the raw-HTML path, which shows pipes).
  if (looksLikeBrevoMarkdown(unescaped)) {
    return { bodyText: cleanInboundMarkdown(unescaped), bodyHtml: null };
  }
  if (looksLikeHtml(unescaped)) {
    // genuinely raw HTML body — pane sanitises before rendering
    return { bodyText: '', bodyHtml: unescaped };
  }
  // plain text — strip any stray noise, pane linkifies bare URLs
  return { bodyText: cleanInboundMarkdown(unescaped), bodyHtml: null };
}

/** PURE — short AI label for the list chip ("lead" / "auto / promo"). */
export function aiLabelShort(label: string): string {
  switch (label) {
    case 'probable_lead':
      return 'lead';
    case 'probable_partner':
      return 'partner';
    case 'probable_vendor':
      return 'vendor';
    case 'probable_service_auto':
      return 'auto / promo';
    default:
      return 'unknown';
  }
}

/** PURE — full AI label for the triage strip ("probable lead · high"). */
export function aiLabelLong(label: string, confidence: string): string {
  const human = String(label || 'unknown_intent').replace(/_/g, ' ');
  return `${human} · ${confidence || 'low'}`;
}

/**
 * PURE — a promo / automated email (no human to reply to). Drives the red
 * triage avatar + glyph; a real person (lead/partner/vendor) gets a blue
 * avatar with initials.
 */
export function isPromoEmail(label: string): boolean {
  return label === 'probable_service_auto' || label === 'unknown_intent';
}

/**
 * PURE — derive a usable subject for a minimal triage doc that never stored
 * one. Use the first non-empty line of the (unescaped, de-HTML'd) body as a
 * subject snippet so the read pane + list show something meaningful instead
 * of a bare "(no subject)". Caps to a sane length.
 */
export function deriveSubject(d: DraftContact, bodyText: string): string {
  if (d.subject) return d.subject;
  const src = bodyText || unescapeHtmlEntities(String(d.firstMessage || ''));
  const firstLine = src
    .replace(/<[^>]+>/g, ' ') // strip any tags if we fell back to HTML
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l.length > 0);
  if (!firstLine) return '(no subject)';
  return firstLine.length > 90 ? `${firstLine.slice(0, 89)}…` : firstLine;
}

/** PURE — map a `draft_contacts` email doc to the carried email payload. */
export function draftToEmailItem(d: DraftContact): EmailItem {
  const { bodyText, bodyHtml } = deriveEmailBody(d);
  return {
    draftId: d.id,
    senderEmail: d.senderEmail || d.email || '',
    senderName: d.senderName ?? (d.name || null),
    receivedAtAddress: d.receivedAtAddress || CARE_EMAIL,
    subject: deriveSubject(d, bodyText),
    bodyText,
    bodyHtml,
    messageId: d.messageId || '',
    aiLabel: d.aiLabel || 'unknown_intent',
    aiConfidence: d.aiConfidence || 'low',
    autoArchived: !!d.autoArchived,
    autoArchivedReason: d.autoArchivedReason ?? null,
    triageAction: d.triageAction ?? null,
    knownContactId: d.knownContactId ?? null,
    assignedTo: d.assignedTo ?? null,
    assignedToName: d.assignedToName ?? null,
    receivedAtMs: toMs(d.lastMessageAt) ?? toMs(d.createdAt),
  };
}

/**
 * PURE — map a `draft_contacts` email doc to an `InboxConversation` so the
 * existing S1 list renders it. Email rows carry `channel:'email'` + `email`
 * and have NO transport `raw` row. A known contact (senderEmail matched a
 * client) is NOT a red-triage row — it shows a neutral "known" chip instead.
 *
 * `unread` clears two ways: the PERSISTED `read` flag on the draft (written by
 * markEmailRead when the email is opened — survives refresh), OR the
 * session-scoped set of keys opened this load (optimistic, pre-write). Either
 * one zeroes the badge so the "1" does not return on reload.
 */
export function draftToInboxConversation(
  d: DraftContact,
  openedKeys?: ReadonlySet<string>,
): InboxConversation {
  const em = draftToEmailItem(d);
  const known = !!em.knownContactId;
  const displayName = em.senderName || em.senderEmail || 'Unknown sender';
  const isRead = !!d.read || !!(openedKeys && openedKeys.has(d.id));
  const unread = isRead ? 0 : 1;
  return {
    key: d.id,
    channel: 'email',
    subject: em.subject,
    email: em,
    name: displayName,
    phone: null,
    initials: isPromoEmail(em.aiLabel) && !known ? '✉' : emailInitials(displayName),
    // Snippet from the rendered text; HTML-only bodies have no bodyText, so
    // strip tags off the HTML for a readable one-line preview.
    snippet: emailSnippet(
      em.bodyText || (em.bodyHtml ? em.bodyHtml.replace(/<[^>]+>/g, ' ') : ''),
    ),
    lastMessageAt: em.receivedAtMs,
    unread,
    // Unmatched (no known contact) → red triage avatar + Triage chip.
    triage: !known,
    aiHandled: false,
    aiBot: false,
    aiPaused: false,
    projectId: null,
    projectRef: null,
    contactId: em.knownContactId || null,
    waProfileName: null,
    number: null,
    receivingPhoneNumberId: null,
    // Email assignment is a real draft-doc write; surface it as the owner chip.
    owner: em.assignedTo
      ? { uid: em.assignedTo, name: em.assignedToName || em.assignedTo }
      : undefined,
  };
}

/* ───────────────────────── channel lens ───────────────────────── */

/** PURE — the lane a row belongs to (absent channel = WhatsApp). */
export function rowChannel(c: InboxConversation): 'whatsapp' | 'email' {
  return c.channel === 'email' ? 'email' : 'whatsapp';
}

/** PURE — filter rows to the active channel lens. */
export function applyChannelFilter(
  rows: InboxConversation[],
  channel: InboxChannel,
): InboxConversation[] {
  if (channel === 'all') return rows;
  return rows.filter((c) => rowChannel(c) === channel);
}

/** PURE — per-channel counts for the toggle badges. */
export function channelCounts(rows: InboxConversation[]): {
  all: number;
  whatsapp: number;
  email: number;
} {
  let whatsapp = 0;
  let email = 0;
  for (const c of rows) {
    if (rowChannel(c) === 'email') email += 1;
    else whatsapp += 1;
  }
  return { all: rows.length, whatsapp, email };
}

/* ───────────────────────── reply contract ───────────────────────── */

export interface EmailReplyParams {
  to: string;
  from: string;
  subject: string;
  inReplyTo: string;
  body: string;
}

/** PURE — "Re: " prefix without doubling it on an already-Re subject. */
export function reSubject(subject: string): string {
  const s = String(subject || '').trim();
  return /^re:/i.test(s) ? s : `Re: ${s || '(no subject)'}`;
}

/**
 * PURE — build the `sendEmailToClient` (no-project) payload for a reply.
 * Matches the Shared Data Contract reply shape (spec §3.4).
 */
export function buildReplyParams(em: EmailItem, body: string): EmailReplyParams {
  return {
    to: em.senderEmail,
    from: CARE_EMAIL,
    subject: reSubject(em.subject),
    inReplyTo: em.messageId,
    body,
  };
}
